import { wait } from "@/mono/lib/common";
import useEvent from "@/mono/lib/hooks/useEvent";
import { useTicker } from "@/mono/lib/hooks/useRaf";
import { useGlobalStore } from "@/mono/store/global";
import { useMemo, useRef } from "react";
import { PlaneGeometry } from "three";
import {
  MEET_IMAGE_START_TICKER,
  MEET_IMAGE_STOP_TICKER,
  MEET_IMAGE_RESET,
} from ".";

import useResize from "@/mono/lib/hooks/useResize";
import NineGLImageElement from "../gl/NineGLImage/NineGLImageElement";

const vertexShader = /*glsl*/ `
    varying vec2 vUv;
    varying vec2 vImageUV;
    varying float vTest;

    uniform float uTime;
    uniform float uFrequency;
    uniform float uAmplitude;
    uniform vec2 uImageDimensions;

    float parabola(float x, float k) {
      return pow(4.0 * x * (1.0 - x), k);
    }

    void main() {
      vec4 ndc = projectionMatrix * modelViewMatrix * vec4(position, 1.0);

      // ndc to screen space
      vec2 screen = (ndc.xy / ndc.w) * 0.5 + 0.5;
      vec3 pos = position;
      
      //TEMP:
      pos.z -= parabola(fract((screen.x) * uFrequency), 2.0) * uAmplitude;
      
      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);

      vUv = uv;
      // Precompute image UVs similar to default shader path
      vImageUV = (.5 + (uv - .5) / uImageDimensions);
    }
  `;

function MeetImageDOM({ i, getBoundsX, getOffsetX, src, ...props }) {
  const wrapperRef = useRef();
  const glImage = useRef();
  const x = useRef(0);
  const offsetX = useRef(0);
  const isBeforeX = useRef(false);
  const isMobileLayout = useGlobalStore((state) => state.isMobileLayout);

  function handleBoundsX() {
    if (!wrapperRef.current) return;
    const viewportOffsetX = getBoundsX();
    if (!viewportOffsetX) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const maxXLeft = rect.left + rect.width / 2;
    const viewportPx = window.innerWidth;
    isBeforeX.current = maxXLeft < -rect.width;

    if (isBeforeX.current) {
      const parentRect =
        wrapperRef.current.parentElement?.getBoundingClientRect();
      const parentWidth = parentRect?.width || viewportPx;
      const gap =
        parseFloat(
          getComputedStyle(wrapperRef.current).marginRight
        ) || 0;
      offsetX.current += parentWidth + gap;
      isBeforeX.current = false;
    }
  }

  const { start: startTicker, stop: stopTicker } = useTicker(({ delta }) => {
    if (!wrapperRef.current) return;
    const speed = isMobileLayout ? 0.08 : 0.125;
    x.current = x.current - speed * (delta * 1000);
    wrapperRef.current.style.transform = `translate(${x.current + offsetX.current}px, calc(var(--offset-y) * var(--transformMultiplier)))`;
    handleBoundsX();
  });

  useEvent(MEET_IMAGE_START_TICKER, async () => {
    startTicker();
    await wait(Math.random() * 400);
    glImage.current?.animateIn?.();
  });

  useEvent(MEET_IMAGE_STOP_TICKER, async () => {
    stopTicker();
    glImage.current?.animateOut?.({ duration: 0 });
    await wait(100);
  });

  useEvent(MEET_IMAGE_RESET, () => {
    x.current = 0;
    offsetX.current = 0;
    isBeforeX.current = false;
    if (wrapperRef.current) {
      wrapperRef.current.style.transform = `translate(0px, calc(var(--offset-y) * var(--transformMultiplier)))`;
    }
  });

  const { className: cn, ...restProps } = props;

  return (
    <div
      ref={wrapperRef}
      className={cn}
      style={{ visibility: "visible" }}
    >
      <NineGLImageElement
        src={src}
        ref={glImage}
        isLink={false}
        animateOnScroll={false}
        useHover={false}
        {...restProps}
      />
    </div>
  );
}

function MeetImageGL({
  i,
  getBoundsX,
  getOffsetX,
  getViewportHalfX,
  src,
  ...props
}) {
  const glImage = useRef();

  const offsetX = useRef(0);

  const x = useRef(0);
  const _x = useRef(0);

  const isBeforeX = useRef(false);

  function handleBoundsX() {
    const g = glImage.current?.group;
    const mesh = glImage.current?.shaderObject?.mesh;
    if (!g || !mesh || !g.parent) return;

    const period = getBoundsX();
    if (!period) return;

    const viewportHalfX = getViewportHalfX?.() || 0;
    const trackedX = g.parent.position.x;
    const animatedX = g.position.x;
    const halfWidth = mesh.scale.x / 2;
    const rightEdge = trackedX + animatedX + halfWidth;

    isBeforeX.current = rightEdge < -viewportHalfX;

    if (isBeforeX.current) {
      offsetX.current += period;
      isBeforeX.current = false;
    }
  }

  const { start: startTicker, stop: stopTicker } = useTicker(({ delta }) => {
    if (!glImage?.current?.shaderObject?.mesh) return;

    const speed = isMobileLayout ? 0.08 : 0.125;

    x.current = x.current - speed * (delta * 1000);

    glImage.current.group.position.x = x.current + offsetX.current;

    handleBoundsX();
  });

  const initial = useRef(false);

  useEvent(MEET_IMAGE_START_TICKER, async () => {
    if (!initial.current) {
      initial.current = true;
    }

    startTicker();
    await wait(Math.random() * 400);

    if (glImage?.current) glImage.current?.animateIn?.();
  });
  useEvent(MEET_IMAGE_STOP_TICKER, async () => {
    stopTicker();

    if (glImage?.current) glImage.current?.animateOut?.({ duration: 0 });
    await wait(100);
  });

  useEvent(MEET_IMAGE_RESET, () => {
    if (!glImage.current) return;

    x.current = 0;
    _x.current = 0;
    offsetX.current = 0;
    isBeforeX.current = false;

    if (glImage.current.group) {
      glImage.current.group.position.x = 0;
      glImage.current.group.rotation.set(0, 0, 0);
      glImage.current.group.scale.setScalar(1);
    }

    handleBoundsX();
  });

  const geometry = useMemo(() => {
    return new PlaneGeometry(1, 1, 20, 20);
  }, []);

  const isMobileLayout = useGlobalStore((state) => state.isMobileLayout);

  const uniforms = {
    uFrequency: {
      value: isMobileLayout ? 0.5 : 0.9,
    },
    uAmplitude: {
      value: isMobileLayout ? 50.0 : 150.0,
    },
  };

  useResize(
    () => {
      if (!glImage.current?.shaderObject) return;

      glImage.current.shaderObject.set(
        "uFrequency",
        isMobileLayout ? 0.5 : 0.9
      );
      glImage.current.shaderObject.set(
        "uAmplitude",
        isMobileLayout ? 50.0 : 150.0
      );
    },
    {
      immediate: true,
      throttle: 100,
    },
    [glImage.current]
  );

  return (
    <NineGLImageElement
      geometry={geometry}
      src={src}
      ref={glImage}
      position-z={i}
      vertexShader={vertexShader}
      isLink={false}
      animateOnScroll={false}
      useHover={false}
      uniforms={uniforms}
      scrollSceneConfig={{
        overrideVisible: true,
      }}
      {...props}
    />
  );
}

export function MeetImage(props) {
  const noWebGLImages = useGlobalStore((s) => s.noWebGLImages);
  if (noWebGLImages) return <MeetImageDOM {...props} />;
  return <MeetImageGL {...props} />;
}
